import { Article, Cart, CartLine, Collection, Image, Money, Product, ProductVariant } from "../types";

export interface Edge<T> {
    node: T;
}
export interface Connection<T> {
    edges: Edge<T>[];
}

export function fromEdges<T>(connection: Connection<T> | null | undefined): T[] {
    return connection?.edges.map((e) => e.node) ?? [];
}

interface ShopifyMoney {
    amount: string;
    currencyCode: string;
}

function mapMoney(money: ShopifyMoney): Money {
    return { amount: money.amount, currencyCode: money.currencyCode };
}

interface ShopifyImage {
    url: string;
    altText: string | null;
    width?: number | null;
    height?: number | null;
}

function mapImage(image: ShopifyImage | null | undefined): Image | null {
    if (!image) return null;
    return { url: image.url, altText: image.altText, width: image.width ?? undefined, height: image.height ?? undefined };
}

interface ShopifyProductVariant {
    id: string;
    title: string;
    availableForSale: boolean;
    quantityAvailable: number | null;
    sku: string | null;
    price: ShopifyMoney;
    compareAtPrice: ShopifyMoney | null;
    selectedOptions: { name: string; value: string }[];
    image: ShopifyImage | null;
}

function mapVariant(variant: ShopifyProductVariant): ProductVariant {
    return {
        id: variant.id,
        title: variant.title,
        availableForSale: variant.availableForSale,
        quantityAvailable: variant.quantityAvailable ?? 0,
        price: mapMoney(variant.price),
        compareAtPrice: variant.compareAtPrice ? mapMoney(variant.compareAtPrice) : null,
        selectedOptions: variant.selectedOptions,
        image: mapImage(variant.image),
        sku: variant.sku,
    };
}

export interface ShopifyProduct {
    id: string;
    handle: string;
    title: string;
    description: string;
    descriptionHtml: string;
    productType: string;
    tags: string[];
    vendor: string;
    availableForSale: boolean;
    createdAt: string;
    featuredImage: ShopifyImage | null;
    images: Connection<ShopifyImage>;
    priceRange: { minVariantPrice: ShopifyMoney; maxVariantPrice: ShopifyMoney };
    compareAtPriceRange: { minVariantPrice: ShopifyMoney; maxVariantPrice: ShopifyMoney } | null;
    options: { id: string; name: string; values: string[] }[];
    variants: Connection<ShopifyProductVariant>;
}

export function mapProduct(product: ShopifyProduct): Product {
    return {
        id: product.id,
        handle: product.handle,
        title: product.title,
        description: product.description,
        descriptionHtml: product.descriptionHtml,
        productType: product.productType,
        tags: product.tags,
        vendor: product.vendor,
        images: fromEdges(product.images).map((img) => mapImage(img)!).filter(Boolean),
        featuredImage: mapImage(product.featuredImage),
        priceRange: {
            minVariantPrice: mapMoney(product.priceRange.minVariantPrice),
            maxVariantPrice: mapMoney(product.priceRange.maxVariantPrice),
        },
        compareAtPriceRange: product.compareAtPriceRange
            ? {
                  minVariantPrice: mapMoney(product.compareAtPriceRange.minVariantPrice),
                  maxVariantPrice: mapMoney(product.compareAtPriceRange.maxVariantPrice),
              }
            : null,
        options: product.options,
        variants: fromEdges(product.variants).map(mapVariant),
        availableForSale: product.availableForSale,
        createdAt: product.createdAt,
    };
}

export interface ShopifyCollection {
    id: string;
    handle: string;
    title: string;
    description: string;
    image: ShopifyImage | null;
}

export function mapCollection(collection: ShopifyCollection): Collection {
    return {
        id: collection.id,
        handle: collection.handle,
        title: collection.title,
        description: collection.description,
        image: mapImage(collection.image),
    };
}

const WORDS_PER_MINUTE = 200;

function estimateReadingMinutes(html: string): number {
    const text = html.replace(/<[^>]+>/g, " ");
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(wordCount / WORDS_PER_MINUTE));
}

/** Parses a `custom.related_products` list-of-product-references metafield, if the store defines one. */
function parseRelatedProductHandles(metafieldValue: string | null | undefined): string[] {
    if (!metafieldValue) return [];
    try {
        const parsed = JSON.parse(metafieldValue);
        return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
    } catch {
        return [];
    }
}

export interface ShopifyArticle {
    id: string;
    handle: string;
    title: string;
    excerpt: string | null;
    contentHtml: string;
    publishedAt: string;
    tags: string[];
    image: ShopifyImage | null;
    author: { name: string };
    blog: { handle: string; title: string };
    relatedProducts?: { value: string } | null;
}

export function mapArticle(article: ShopifyArticle): Article {
    return {
        id: article.id,
        handle: article.handle,
        title: article.title,
        excerpt: article.excerpt ?? "",
        contentHtml: article.contentHtml,
        image: mapImage(article.image),
        publishedAt: article.publishedAt,
        tags: article.tags,
        author: { name: article.author?.name ?? "" },
        blog: article.blog,
        relatedProductHandles: parseRelatedProductHandles(article.relatedProducts?.value),
        readingMinutes: estimateReadingMinutes(article.contentHtml),
    };
}

interface ShopifyCartLine {
    id: string;
    quantity: number;
    cost: { totalAmount: ShopifyMoney };
    merchandise: {
        id: string;
        title: string;
        image: ShopifyImage | null;
        price: ShopifyMoney;
        product: { id: string; handle: string; title: string };
    };
}

function mapCartLine(line: ShopifyCartLine): CartLine {
    return {
        id: line.id,
        quantity: line.quantity,
        merchandise: {
            id: line.merchandise.id,
            title: line.merchandise.title,
            image: mapImage(line.merchandise.image),
            price: mapMoney(line.merchandise.price),
            product: line.merchandise.product,
        },
        cost: { totalAmount: mapMoney(line.cost.totalAmount) },
    };
}

export interface ShopifyCart {
    id: string;
    checkoutUrl: string;
    totalQuantity: number;
    cost: { subtotalAmount: ShopifyMoney; totalAmount: ShopifyMoney };
    lines: Connection<ShopifyCartLine>;
}

export function mapCart(cart: ShopifyCart): Cart {
    return {
        id: cart.id,
        checkoutUrl: cart.checkoutUrl,
        totalQuantity: cart.totalQuantity,
        cost: {
            subtotalAmount: mapMoney(cart.cost.subtotalAmount),
            totalAmount: mapMoney(cart.cost.totalAmount),
        },
        lines: fromEdges(cart.lines).map(mapCartLine),
    };
}
